import Image from 'next/image';
import Link from 'next/link';
import type { ReactNode } from 'react';
import { Countdown } from '@/components/Countdown';
import type { PoweredBy } from '@/types/sponsor';
import { cn } from '@/utils/cn';

type TopWinnerPlayer = {
  userName?: string;
  points?: number;
};

export function TopWinnerCards({ players }: { players: TopWinnerPlayer[] }) {
  const [firstAtLeaderboard, ...rest] = players;
  return (
    <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-8 lg:gap-12">
      {firstAtLeaderboard && (
        <div className="col-span-1 flex items-center justify-center sm:col-span-2">
          <div className="w-full max-w-md">
            <TopWinnerCard index={0} player={firstAtLeaderboard} />
          </div>
        </div>
      )}

      {rest.slice(0, 4).map((player, index) => (
        // NOTE: user.id is null for some reason
        <div className="w-full" key={player.userName}>
          <TopWinnerCard index={index + 1} player={player} />
        </div>
      ))}
    </div>
  );
}

export const TopWinnerCard = ({
  player,
  index,
}: {
  player: TopWinnerPlayer;
  index: number;
}) => {
  return (
    <SkewedCard className="flex flex-col justify-end px-4 pb-8">
      <div
        className={cn(
          '-skew-x-12 -top-6 -translate-x-1/2 absolute left-1/2',
          'flex size-12 items-center justify-center',
          'border border-light-blue/15 bg-pickems-winners-green-gradient font-semibold text-xl'
        )}
      >
        <span className="skew-x-12">{index + 1}.</span>
      </div>

      <p className="mb-1 truncate pt-10 text-center font-semibold text-lg">
        {player.userName}
      </p>
      <p className="text-center font-semibold text-light-blue text-sm">
        {player.points} points
      </p>
    </SkewedCard>
  );
};

export const CountdownCard = ({
  countdownDatetime,
  countdownLabel,
}: {
  countdownDatetime: Date;
  countdownLabel: string;
}) => {
  return (
    <SkewedCard className="p-4 text-center md:p-6">
      <p className="mb-1 whitespace-nowrap text-gray text-xs md:text-sm">
        {countdownLabel}
      </p>
      <Countdown targetDate={countdownDatetime} />
    </SkewedCard>
  );
};

export const SponsorCard = ({ poweredBy }: { poweredBy: PoweredBy }) => {
  return (
    <Link href={poweredBy.url} target="_blank">
      <SkewedCard className="p-4 text-center md:p-6">
        <Image
          alt={poweredBy.name}
          className="w-24 object-contain md:w-32"
          height={poweredBy.logo.height}
          src={poweredBy.logo.url}
          width={poweredBy.logo.width}
        />
      </SkewedCard>
    </Link>
  );
};

const SkewedCard = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn(
        '-skew-x-12 relative w-full rounded-lg border border-blue-gray/50',
        className
      )}
    >
      <div className="absolute inset-0 rotate-180 rounded-lg bg-custom-gradient-2 shadow-prizeNumber" />
      <div className="skew-x-12">{children}</div>
    </div>
  );
};
